import { useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  BookmarkIcon,
  EditIcon,
  FlagIcon,
  MoreVerticalIcon,
  ShareIcon,
  Trash2Icon,
} from 'lucide-react';
import { ShareButton } from './ui/share-button';
import React, { memo } from 'react';
import { useReadLocation, useStableNavigate } from '@hooks/useStableRouter';
import { useDeleteQuestionApiV1QuestionsQuestionIdDelete } from '@api-client/api-client';
import { useToast } from '@hooks/use-toast';
import { logger } from '@utils/logger';
import { useAuth } from '@providers';
import { formatCompactTimeAgo } from '@utils/datetime';
import { canCrosspostQuestion, canDeleteQuestion, canEditQuestion } from '@utils/permissions';
import { RichTextDisplay } from './ui';
import { Button } from './ui/button';
import { Card, CardContent, CardFooter } from './ui/card';
import { ReportDialog } from './ui/report-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { PinIndicatorIcon } from './icons/PinIndicatorIcon';
import { PinActionIcon } from './icons/PinActionIcon';
import { PinConfirmDrawer } from './PinConfirmDrawer';
import { spaceQuestionsFeedKey } from '@lib/cache/questions';
import { usePinQuestion } from '@hooks/usePinQuestion';
// AIDEV-NOTE: Switched to unified interactions hook
import { QuestionVisibility } from '@models/api/question';
import type { CommunityContext } from '../types/community';
import {
  invalidateQuestionCaches as invalidateQuestionCachesCentral,
  pinnedQuestionsSpaceKey,
  type QuestionCacheItem,
} from '@lib/cache/questions';
import { clearJustPosted } from '@lib/feed/justPosted';
// AIDEV-NOTE: QuestionCard redesign - stacked avatars and event badges
import { AskedOnYourBehalfIndicator } from './ui/AskedOnYourBehalfIndicator';
import { QuestionAuthorAvatar, QuestionAuthorName } from './ui/QuestionAuthorByline';
import { SpaceIndicator } from './SpaceIndicator';
import type { SpaceIndicatorItem } from './SpaceIndicator';
import { SpacesSheet } from './SpacesSheet';
import { buildSpaceEventItems } from '@utils/buildSpaceEventItems';
import { TopicBadge } from './community/TopicBadge';
import { HelpableFeedChip } from './live/HelpableFeedChip';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useReport } from '@hooks/useReport';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import {
  buildEventContextPayload,
  buildEventParentSpacePayload,
  buildSpaceContextPayload,
} from '@lib/analytics/payloads';
import { useQuestionInteractionsOptimistic } from '@hooks/useQuestionInteractionsOptimistic';
import { ShareScenario } from '@utils/shareHelpers';
import { hasImpressed, markImpressed } from '@lib/analytics/impressions';
import { getProfilePath, getQuestionEditPath, getQuestionPath } from '@utils/slugHelpers';
import type { EditPostLocationState } from '@utils/create-question/editPostLocationState';
import { routes } from '@routes';
import { ComingSoonModal } from './ComingSoonModal';
import { CrosspostModal } from './CrosspostModal';
import { getAskOnBehalfView } from '@utils/askOnBehalf';
import { postAuthorChat } from '@utils/postAuthorChat';
import type { ChatRouteState } from '@models/view';
import { LoginPromptModal } from './LoginPromptModal';
import { useLoginPrompt, type LoginPromptActionType } from '@hooks/useLoginPrompt';
import { useShareSheet } from '@hooks/useShareSheet';
import { ConfirmDeleteDrawer } from './ui/confirm-delete-drawer';
import { QuestionInteractionRow } from './ui/QuestionInteractionRow';
import { isAxiosError } from 'axios';

/**
 * Custom styles for SVG icon color filtering
 */
const customStyles = `
  .filter-orange {
    filter: brightness(0) saturate(100%) invert(63%) sepia(38%) saturate(5084%) hue-rotate(6deg) brightness(101%) contrast(103%);
  }
`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const data = error.response?.data as unknown;
    if (isRecord(data) && typeof data.detail === 'string') {
      return data.detail;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'unknown';
};

/**
 * Question card props
 */
interface QuestionCardProps {
  question: QuestionCacheItem;
  positionInFeed?: number;
  sourceContext?: 'feed' | 'profile' | 'helper_feed';
  /**
   * Fires when the user offers help from this card (the "I can help" action),
   * alongside the standard `help_offered` analytics + chat navigation. A pure
   * notification so a parent can react to the standard act (e.g. the helper feed
   * marks the question seen). Default undefined → existing callers unaffected.
   */
  onHelpOffered?: () => void;
  /** When set, all interaction buttons call this instead of their normal handlers (preview mode) */
  onInteractionBlocked?: () => void;
  /**
   * When true, NO tap on this card may navigate: the card body, the space/event chip and its
   * sheet, the author and beneficiary names, the ask-on-behalf footer's OP link, the edit item
   * and the chat "I can help" opens all funnel instead. Use in gated previews (e.g. the guest
   * event preview) so nothing on the card walks the viewer out of the gate into a public route.
   *
   * With `onInteractionBlocked` set, every one of those taps calls it. Without it, a guest gets
   * the card's own sign-in prompt and a signed-in viewer's tap is suppressed (see
   * `consumeGatedNavigation`). Defaults to false: normal navigation for every other caller.
   */
  blockNavigation?: boolean;
  /** When set, navigations append ?returnTo so the back button returns to the originating page */
  returnTo?: string;
  /** When provided, replaces the default author header (used by profile context). */
  header?: React.ReactNode;
  /**
   * When rendered inside a community/space detail feed, identifies the current space and
   * the viewer's admin status. Enables the pin badge + pin/unpin dropdown items.
   */
  communityContext?: CommunityContext;
  /**
   * True when this post is one the viewer can help with at a LIVE event (Track 2
   * Phase A, spec P4/P11): its id is in `useEventHelpable`'s items for the event
   * feed's event, or the space's live event. Renders the small "you can help"
   * chip in the byline. The event/space feed hosts own the match (id ∈ items) so
   * every other caller (Home, profile, helper feed) leaves it undefined and is
   * byte-identical. Anonymous posts NEVER wear it - guarded here defensively even
   * though the backend already excludes them from items (the anonymity invariant).
   */
  isHelpable?: boolean;
  /**
   * Set false for a non-feed rendering of the card (e.g. the activation kit's space preview) so it
   * never arms the impression observer: no `post_impression`, and no dedup entry that would
   * suppress the post's real impression later. Defaults to true: every other caller is unchanged.
   */
  trackImpressions?: boolean;
}

/**
 * Question card component displaying individual questions in the feed
 *
 * Best practices applied:
 * - Wrapped with memo() to prevent unnecessary re-renders in lists
 * - Named function for better React DevTools debugging
 */
export const QuestionCard = memo(function QuestionCard({
  question,
  positionInFeed,
  sourceContext = 'feed',
  onHelpOffered,
  onInteractionBlocked,
  blockNavigation = false,
  returnTo,
  header,
  communityContext,
  isHelpable,
  trackImpressions = true,
}: QuestionCardProps) {
  const detail = question.detail ?? null;
  const { user, isAuthenticated } = useAuth();
  // AIDEV-NOTE: Chat state now managed by React Query hooks - removed unused destructuring
  const { toast } = useToast();
  // Deliberately NOT useNavigate/useLocation: both make this card a router-context consumer, and
  // React wakes context consumers regardless of memo() or identical props. Twenty of these are
  // mounted at once on the event feed, and a single `?tab=` replace re-rendered all twenty of them
  // (measured) on the very tap that navigates AWAY from that feed. See stableRouterContext.ts.
  // `readLocation()` must be called inside a handler, never during render:
  // this component no longer re-renders when the URL moves, so a render-time read would freeze at
  // mount and stamp a `returnTo` pointing at a page the user has already left.
  const navigate = useStableNavigate();
  const readLocation = useReadLocation();
  const queryClient = useQueryClient();
  const relatedEventIds = React.useMemo(() => {
    return question.event_summaries.map(e => e.id).filter((id): id is string => !!id);
  }, [question.event_summaries]);

  const userId = user?.id;

  // Primitive deps for space fallback (avoids object reference in useMemo deps)
  const spaceId = detail?.space?.id;
  const spaceName = detail?.space?.name;
  const spaceImageUrl = detail?.space?.image_url;

  // "you can help" byline chip (Track 2 Phase A, spec P4/P11): the event/space
  // feed host passes `isHelpable` when this post is one of the viewer's judged
  // matches at the live event. Anonymous posts NEVER wear it - the backend
  // already excludes them from the helpable set, but the guard here is the
  // defensive belt for the anonymity invariant (a matched id can never surface
  // an author). One element, reused in both byline variants below.
  const showHelpableChip = !!isHelpable && !question.is_anonymous;
  const helpableChip = showHelpableChip ? <HelpableFeedChip /> : null;

  // Combined spaces + events for the inline indicator (spaces first, then events).
  // Live Layer v1 (L12): a boosted post carries `author_live_event`, prepended
  // as the live "at {event}" first item.
  //
  // ANONYMITY DOUBLE-GUARD: never surface the live chip on an anonymous post.
  // The backend already suppresses `author_live_event` for anon posts; passing
  // null here mirrors that as defense-in-depth so a future contract regression
  // (the #342 class) can't leak the author's live-event presence. Keyed off
  // `question.is_anonymous` — the same signal the whole card uses to mask the
  // author's identity/avatar/name.
  const authorLiveEvent = detail?.author_live_event ?? null;
  const spaceEventItems = React.useMemo(
    () =>
      buildSpaceEventItems(
        question.space_summaries,
        question.event_summaries,
        spaceId && spaceName ? { id: spaceId, name: spaceName, image_url: spaceImageUrl } : null,
        question.is_anonymous ? null : authorLiveEvent,
      ),
    [
      question.space_summaries,
      question.event_summaries,
      spaceId,
      spaceName,
      spaceImageUrl,
      question.is_anonymous,
      authorLiveEvent,
    ],
  );

  const shouldInvalidateMeetverse = question.visibility === QuestionVisibility.anyone;

  // Local state
  const [isReportModalOpen, setIsReportModalOpen] = React.useState(false);
  const [isDeleteDrawerOpen, setIsDeleteDrawerOpen] = React.useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = React.useState(false);
  const reportMutation = useReport();
  const [isCrosspostModalOpen, setIsCrosspostModalOpen] = React.useState(false);
  const [isSpacesSheetOpen, setIsSpacesSheetOpen] = React.useState(false);
  const [isPinDrawerOpen, setIsPinDrawerOpen] = React.useState(false);
  const [isUnpinDrawerOpen, setIsUnpinDrawerOpen] = React.useState(false);

  const { pin, unpin, isPinning, isUnpinning } = usePinQuestion();

  // Community context primitives: extracted so callbacks/memos can depend on
  // string values rather than the parent object reference (matches the
  // "Primitive deps for space fallback" pattern at lines 147-149).
  const communitySpaceId = communityContext?.spaceId;
  const communitySpaceIdentifier = communityContext?.spaceIdentifier;
  const communitySpaceName = communityContext?.spaceName;
  // Pin management is admin-gated and admins are always approved members, so the
  // populated unfiltered feed cache is the member one. Default to the member
  // variant when membership is unknown (e.g. the Home surface omits it) — that key
  // simply misses → the drawer renders without the counter (backend 409 still gates).
  const communitySpaceIsMember = communityContext?.isMember ?? true;

  // Subscribe to the space-scoped pinned IDs cache entry. This is the
  // authoritative source for pin state — entity's pinned_at can be clobbered
  // by cross-feed upserts (e.g. Home my-spaces). queryFn reads back the value
  // set by useInfiniteSpaceQuestions/usePinQuestion via setQueryData.
  const { data: spacePinnedIds } = useQuery<string[] | null>({
    queryKey: pinnedQuestionsSpaceKey(communitySpaceId ?? ''),
    enabled: Boolean(communitySpaceId),
    queryFn: () =>
      Promise.resolve(
        queryClient.getQueryData<string[]>(pinnedQuestionsSpaceKey(communitySpaceId ?? '')) ?? null,
      ),
    staleTime: Infinity,
  });

  // Live pin count for the confirm drawer's capacity hint. Reads the unfiltered
  // space feed cache directly via the producer-owned key helper. Topic-filtered
  // caches omit pins outside the filter (spec: "topic filter applies to
  // pinned"), so we ignore them rather than underreport. Returns `undefined`
  // when no unfiltered cache is populated or when the cache holds zero
  // questions — the drawer then renders without the counter and the backend
  // 409 remains the authoritative cap gate.
  const currentPinCount = React.useMemo<number | undefined>(() => {
    if (!isPinDrawerOpen || !communitySpaceId) return undefined;
    const firstPage = queryClient.getQueryData<InfiniteData<{ ids: string[] }>>(
      spaceQuestionsFeedKey(communitySpaceId, communitySpaceIsMember),
    )?.pages[0];
    if (!firstPage || firstPage.ids.length === 0) return undefined;
    return firstPage.ids.filter(id => spacePinnedIds?.includes(id)).length;
  }, [queryClient, communitySpaceId, communitySpaceIsMember, isPinDrawerOpen, spacePinnedIds]);

  // spacePinnedIds is nullish (either `null` once the queryFn resolves, or
  // `undefined` before it has resolved at all — React Query's pending-state
  // data is always `undefined` regardless of TData) exactly when the
  // space-scoped cache was never seeded (query disabled, or no unfiltered
  // feed for this space has loaded yet) — fall back to the entity's own
  // pinned_at in that case. Once the query resolves to a real array (even
  // []), that array is authoritative.
  // (queryFn resolves to `null`, not `undefined`, for the seeded-miss case:
  // TanStack Query's queryFn contract rejects an `undefined` resolution as an
  // error — see queryCacheOnError.ts — so `null` is the sentinel that reaches
  // the component as genuinely "not seeded" without tripping the dev-mode
  // warning / onError logging / retry cycle.)
  const resolvedPinState =
    spacePinnedIds != null ? spacePinnedIds.includes(question.id) : Boolean(detail?.pinned_at);
  const showPinBadge = resolvedPinState && Boolean(communityContext?.spaceId);
  // Space admins (communityContext.isAdmin) and platform admins (backend-
  // computed has_extra_options) can moderate pins. Distinct from
  // canDeleteQuestion — question owners can delete but cannot pin.
  const canManagePins = Boolean(communityContext?.isAdmin) || question.has_extra_options;
  const isQuestionPinned = resolvedPinState;

  // Login prompt for unauthenticated users. Hoisted above the gate below, which falls back to it.
  const {
    showPrompt: showLoginPrompt,
    actionType: loginPromptActionType,
    openPrompt: openLoginPrompt,
    closePrompt: closeLoginPrompt,
    handleLogin,
  } = useLoginPrompt();

  /**
   * The gate behind `blockNavigation`. Returns true when this tap was consumed, meaning the
   * caller must NOT navigate.
   *
   * `blockNavigation` means the card sits inside a gated preview, so EVERY navigation target has
   * to route through here rather than just the card body: the question page, the space/event
   * chip, the spaces sheet's items, the author and beneficiary names, the ask-on-behalf footer's
   * OP link, the edit item, and the chat that "I can help" opens. Each of those leaves for a
   * route the caller put the gate up to keep the viewer out of, so while gated there is no
   * navigation this card is still allowed to make. Guarding one target at a time is how the card
   * body ended up the only guarded one.
   *
   * Precedence: a caller-supplied funnel wins; failing that a guest gets the card's own sign-in
   * prompt rather than the caller's (possibly unrelated) funnel.
   */
  const consumeGatedNavigation = React.useCallback(
    (promptAction: LoginPromptActionType) => {
      if (!blockNavigation) return false;
      if (onInteractionBlocked) {
        onInteractionBlocked();
        return true;
      }
      if (!isAuthenticated) {
        openLoginPrompt(promptAction);
        return true;
      }
      // Gated, no funnel, and the viewer is signed in: there is no sign-in prompt to show them and
      // the caller supplied nothing to funnel into, so the tap does nothing. Suppressing is what
      // the prop is named for; navigating instead would leak the one thing it exists to prevent,
      // for exactly the viewers the caller cannot see. Deliberately not a type error: omitting
      // `onInteractionBlocked` is a valid configuration (EventFeedTab does it so a question tap
      // prompts sign-in instead of opening the event's join funnel) and auth state is not knowable
      // at the call site. Today's only caller is EventFeedTab, inside the guest preview drawer that
      // EventDetails renders only once auth has settled to a guest, so this warning is aimed at
      // whoever adds the second one.
      logger.warn(
        '[QuestionCard] blockNavigation is set with no onInteractionBlocked and the viewer is ' +
          'authenticated: the tap was suppressed because there is nothing to funnel them into. ' +
          'Pass onInteractionBlocked if this card can be gated for signed-in viewers.',
      );
      return true;
    },
    [blockNavigation, onInteractionBlocked, isAuthenticated, openLoginPrompt],
  );

  const handleSpaceItemClick = React.useCallback(
    (item: SpaceIndicatorItem) => {
      // Carries the gate itself rather than trusting its callers: it is reached from the spaces
      // sheet as well as from the chip's own handler.
      if (consumeGatedNavigation('generic')) return;
      // Read at tap time so the stamp is where the user actually is, not where this card was
      // rendered. `readLocation` is identity-stable, so this callback is too.
      const { pathname } = readLocation();
      if (item.type === 'space') {
        navigate(routes.community.details({ id: item.id }, { returnTo: pathname }));
        return;
      }
      navigate(routes.event.details({ id: item.id }, { returnTo: pathname }));
    },
    [navigate, readLocation, consumeGatedNavigation],
  );

  // The author id analytics may send: none for an anonymous post, whose author fields are never read
  // (TECH-693), so these metrics read the same before and after TECH-712 nulls them. detail.user_id
  // is absent on QuestionDetailRead; fall back to detail.user.id so author_id analytics don't go
  // null after a detail-page visit merges the entity.
  const analyticsAuthorId = question.is_anonymous
    ? null
    : (detail?.user?.id ?? detail?.user_id ?? null);

  // AIDEV-NOTE: Memoize hook input - question prop is stable from React Query
  const interactionInput = React.useMemo(() => {
    const normalizedEventIds = question.event_summaries
      .map(e => e.id)
      .filter((id): id is string => !!id);

    return {
      id: question.id,
      question: {
        event_ids: normalizedEventIds,
        is_my_question: question.is_my_question,
        user_has_me_too: question.user_has_me_too,
        user_has_i_can_help: question.user_has_i_can_help,
        user_has_bookmarked: question.user_has_bookmarked,
        visibility: question.visibility,
        slug: question.slug,
      },
    };
  }, [question]);

  const {
    userHasUpvoted,
    userHasMeToo,
    userHasICanHelp,
    upliftLimitReached,
    interactionsLoading,
    isOwnQuestion,
    createPending,
    deletePending,
    handleUpvote,
    handleMeToo,
  } = useQuestionInteractionsOptimistic(interactionInput);
  const { openQuestionShareSheet } = useShareSheet();
  const deleteQuestionMutation = useDeleteQuestionApiV1QuestionsQuestionIdDelete();

  const { trackEvent, trackMeaningfulAction } = useAmplitudeTracking();

  const impressionContainerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !trackImpressions) return;

    // AIDEV-NOTE: a card that stays continuously visible across a pathname change produces no
    // second impression. IntersectionObserver only calls back when the intersection CHANGES, so no
    // new dwell begins, and the observer is armed once for the card's lifetime. Re-arming it would
    // mean tearing down and rebuilding one observer per card on every navigation, on the hot path
    // this component was just taken off - and pathname is too noisy a key to re-fire on anyway,
    // since the canonical-slug redirect changes it without changing the surface. Any dwell that
    // BEGINS after the change is keyed correctly.

    const node = impressionContainerRef.current;
    if (!node) return;

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    let lastWasVisible = false;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;

        if (!isVisible) {
          lastWasVisible = false;
          if (dwellTimer) {
            clearTimeout(dwellTimer);
            dwellTimer = null;
          }
          return;
        }

        if (lastWasVisible) return;
        lastWasVisible = true;

        dwellTimer = setTimeout(() => {
          // Composed when the dwell COMPLETES, not when the observer was armed. This card can
          // outlive a pathname change - `/communities/:a` and `/communities/:b` share one route
          // element, and CommunityDetail's canonical-slug redirect replaces the pathname under an
          // already-mounted feed - so a key sampled at arm time can name a surface the viewer has
          // already left, dropping the impression on the one they are actually on. Reading the
          // location here is safe for the same reason the tap handlers read here: the provider
          // syncs its ref in a layout effect. `?tab=` lives in the search string, which this key
          // deliberately ignores - the same post in the same feed is one impression either way.
          const entityId = `${question.id}:${sourceContext}:${readLocation().pathname}`;
          // This check used to sit above, before the observer was built, as an early-out. The two
          // cannot coexist: an early-out needs the key before the observer exists, and the key is
          // only knowable once the dwell lands. Trade-off accepted: a revisited feed now builds one
          // observer per card instead of skipping the already-impressed ones, which is the same
          // construction every cold feed load already pays for all 20. In exchange the check now
          // also catches a post scrolled out and back within a single mount, which the arm-time
          // check could not see and which therefore fired a duplicate impression.
          if (hasImpressed(ANALYTICS_EVENTS.post_impression, entityId)) return;
          markImpressed(ANALYTICS_EVENTS.post_impression, entityId);
          trackEvent(ANALYTICS_EVENTS.post_impression, {
            question_id: question.id,
            author_id: analyticsAuthorId,
            is_anonymous: question.is_anonymous,
            position_in_feed: positionInFeed ?? null,
            source_context: sourceContext,
            // AIDEV-NOTE: Add post_type for unified dashboard queries across all content types
            post_type: 'question',
          });
        }, 1000);
      },
      { threshold: [0, 0.5, 1] },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (dwellTimer) clearTimeout(dwellTimer);
    };
  }, [
    readLocation,
    positionInFeed,
    question.id,
    question.is_anonymous,
    analyticsAuthorId,
    sourceContext,
    trackImpressions,
    trackEvent,
  ]);

  /**
   * Handle "I can help" button click - navigates directly to chat with question context
   * AIDEV-NOTE: Modal removed - chat opens directly with question reference in input
   */
  const handleCanHelp = () => {
    if (!user || isOwnQuestion) {
      return;
    }

    // Someone else's post: an anonymous one is chatted about by the post alone, never through its
    // author fields (TECH-693). Read at tap time, so the chat's back button returns to the URL the
    // offer was made from; the search string keeps any query params (filters, returnTo chains).
    const { pathname, search } = readLocation();
    const chat = postAuthorChat(
      { id: question.id, is_anonymous: question.is_anonymous, user: detail?.user },
      pathname + search,
    );

    trackEvent(ANALYTICS_EVENTS.help_offered, {
      question_id: question.id,
      recipient_user_id: chat.author?.id ?? null,
      is_anonymous: question.is_anonymous,
      source_context: sourceContext,
      position_in_feed: positionInFeed ?? null,
      stage: 'clicked',
      ...buildSpaceContextPayload(question.space_summaries),
      // AIDEV-NOTE: Track if this is the first help offer on the question for "first help received" metric
      is_first_offer_on_question: question.i_can_help_count === 0,
    });

    // Notify the parent of the standard help-offer act (e.g. the helper feed
    // marks the question seen). Pure notification; navigation/analytics below
    // are unchanged.
    onHelpOffered?.();

    // Navigate directly to chat with question context
    navigate(chat.path, {
      state: {
        ...chat.state,
        chatInitialization: {
          questionData: {
            id: question.id,
            title: question.title,
            description: question.content || '',
            authorId: chat.author?.id ?? null,
            authorName: chat.author?.full_name ?? null,
            authorAvatar: chat.author?.profile_picture ?? undefined,
            // AIDEV-NOTE: Chat context only supports single event; using first as primary
            eventId: question.event_summaries[0]?.id ?? null,
            eventName: question.event_summaries[0]?.name || 'Meetverse',
            isAnonymous: question.is_anonymous,
            createdAt: question.created_at,
            // Thread space context so chat-stage `help_offered` analytics
            // carry full attribution even on a cold detail cache.
            spaceSummaries: question.space_summaries,
          },
          mode: 'offer_help' as const,
          hasExistingInteraction: question.user_has_i_can_help,
        },
      } satisfies ChatRouteState,
    });
  };

  // Format the time ago
  const timeAgo = question.created_at ? formatCompactTimeAgo(question.created_at) : '';

  // AIDEV-NOTE: Shared ask-on-behalf derivation (role, claim flags, subtitle).
  // asked_for_user lives in detail on QuestionCacheItem, not flat on question.
  const askOnBehalf = getAskOnBehalfView(
    {
      claim_status: question.claim_status,
      asked_for_user_id: question.asked_for_user_id,
      created_by_user: question.created_by_user,
      asked_for_user: detail?.asked_for_user ?? null,
      asked_for_email: detail?.asked_for_email ?? null,
    },
    user?.id,
  );
  // Feed banner policy: the beneficiary sees it in any pending/claimed state;
  // everyone else only once CLAIMED (attribution). Never on declined.
  const shouldShowAskedOnYourBehalfIndicator =
    askOnBehalf.kind !== 'self' &&
    askOnBehalf.kind !== 'declined' &&
    (askOnBehalf.role === 'beneficiary' || askOnBehalf.kind === 'claimed');

  // Delete question (owner or admin)
  const handleDeleteQuestion = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    trackEvent(ANALYTICS_EVENTS.post_delete_clicked, {
      user_id: user.id,
      question_id: question.id,
      is_anonymous: question.is_anonymous,
      source_context: sourceContext,
      position_in_feed: positionInFeed ?? null,
    });
    setIsDeleteDrawerOpen(true);
  };

  const handleConfirmDeleteQuestion = () => {
    if (!user) return;

    deleteQuestionMutation.mutate(
      { questionId: question.id },
      {
        onSuccess: () => {
          trackEvent(ANALYTICS_EVENTS.post_delete_succeeded, {
            user_id: user.id,
            question_id: question.id,
            is_anonymous: question.is_anonymous,
            source_context: sourceContext,
            position_in_feed: positionInFeed ?? null,
          });
          setIsDeleteDrawerOpen(false);
          // Unregister from the just-posted splice BEFORE invalidating: the refetch
          // below must not re-pin a deleted post to the top of the meetverse feed.
          clearJustPosted(question.id);
          // Invalidate all questions cache (including user-questions)
          invalidateQuestionCachesCentral({
            queryClient,
            eventIds: relatedEventIds,
            shouldInvalidateMeetverse,
            invalidateUserQuestions: true,
          });
          toast({
            title: 'Post deleted',
            description: 'The post was removed.',
          });
        },
        onError: (error: unknown) => {
          logger.error('Failed to delete question:', error);
          setIsDeleteDrawerOpen(false);
          trackEvent(ANALYTICS_EVENTS.post_delete_failed, {
            user_id: user.id,
            question_id: question.id,
            is_anonymous: question.is_anonymous,
            source_context: sourceContext,
            position_in_feed: positionInFeed ?? null,
            error_message: getErrorMessage(error),
          });
          toast({
            title: 'Delete failed',
            description: 'Could not delete post.',
            variant: 'error',
          });
        },
      },
    );
  };

  const handlePinClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!communitySpaceId) return;
      // Hardcode the menu surface so the click event groups by the same
      // source_context as its sibling lifecycle events (`_confirmed`,
      // `_succeeded`, `_failed`) — those flow through usePinQuestion with
      // `sourceContext: 'community_feed_card_menu'`. Falling back to the
      // generic `sourceContext` prop here would split the Amplitude funnel.
      trackEvent(ANALYTICS_EVENTS.post_pin_clicked, {
        space_id: communitySpaceId,
        question_id: question.id,
        source_context: 'community_feed_card_menu',
        position_in_feed: positionInFeed ?? null,
      });
      setIsPinDrawerOpen(true);
    },
    [communitySpaceId, question.id, positionInFeed, trackEvent],
  );

  const handleUnpinClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!communitySpaceId) return;
      // Hardcoded for the same funnel-consistency reason as handlePinClick.
      trackEvent(ANALYTICS_EVENTS.post_unpin_clicked, {
        space_id: communitySpaceId,
        question_id: question.id,
        source_context: 'community_feed_card_menu',
        position_in_feed: positionInFeed ?? null,
      });
      setIsUnpinDrawerOpen(true);
    },
    [communitySpaceId, question.id, positionInFeed, trackEvent],
  );

  const handleConfirmPin = React.useCallback(async () => {
    if (!communitySpaceId) return;
    try {
      await pin({
        spaceId: communitySpaceId,
        spaceIdentifier: communitySpaceIdentifier,
        questionId: question.id,
        questionSlug: question.slug,
        spaceName: communitySpaceName,
        sourceContext: 'community_feed_card_menu',
      });
      setIsPinDrawerOpen(false);
      toast({ title: 'Pinned to the top of the space' });
    } catch {
      // Error toast already shown by usePinQuestion onError handler; keep drawer open to retry.
    }
  }, [
    pin,
    communitySpaceId,
    communitySpaceIdentifier,
    communitySpaceName,
    question.id,
    question.slug,
    toast,
  ]);

  const handleConfirmUnpin = React.useCallback(async () => {
    if (!communitySpaceId) return;
    try {
      await unpin({
        spaceId: communitySpaceId,
        spaceIdentifier: communitySpaceIdentifier,
        questionId: question.id,
        questionSlug: question.slug,
        spaceName: communitySpaceName,
        sourceContext: 'community_feed_card_menu',
      });
      setIsUnpinDrawerOpen(false);
    } catch {
      // Error toast already shown by usePinQuestion onError handler; keep drawer open to retry.
    }
  }, [
    unpin,
    communitySpaceId,
    communitySpaceIdentifier,
    communitySpaceName,
    question.id,
    question.slug,
  ]);

  /**
   * Handle clicking on the question content area
   */
  const handleQuestionClick = () => {
    // Gated preview: never navigate into the (public) question page and escape the gate. A guest
    // with no caller-supplied funnel is prompted to sign in to see the question (the card's own
    // LoginPromptModal), not pushed into a separate event/going funnel.
    if (consumeGatedNavigation('view_private_question')) return;
    trackEvent(ANALYTICS_EVENTS.post_viewed, {
      question_id: question.id,
      author_id: analyticsAuthorId,
      is_anonymous: question.is_anonymous,
      source_context: sourceContext,
      position_in_feed: positionInFeed ?? null,
      is_authenticated: isAuthenticated,
    });
    trackMeaningfulAction('post_viewed', {
      question_id: question.id,
      is_authenticated: isAuthenticated,
    });
    const path = returnTo
      ? routes.question.details(question, { returnTo })
      : getQuestionPath(question);
    navigate(path);
  };

  /**
   * Handle keyboard navigation for accessibility
   */
  const handleQuestionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleQuestionClick();
    }
  };

  /**
   * Handle clicking on the author's profile
   */
  const handleAuthorClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    // Gate first, and consume the event with it, so one tap fires one funnel: without the
    // stopPropagation the tap would bubble to the card body and funnel a second time.
    if (consumeGatedNavigation('connect')) {
      e.stopPropagation();
      return;
    }
    // Ungated surfaces are unchanged: a guest's tap falls through to the card body below.
    if (!isAuthenticated) return;
    trackEvent(ANALYTICS_EVENTS.post_author_clicked, {
      user_id: user?.id ?? null,
      question_id: question.id,
      author_id: analyticsAuthorId,
      is_anonymous: question.is_anonymous,
      is_authenticated: isAuthenticated,
    });
    e.stopPropagation(); // Prevent question click event
    if (!question.is_anonymous && detail?.user) {
      const profilePath = returnTo
        ? routes.profile.view(detail.user, { returnTo })
        : getProfilePath(detail.user);
      navigate(profilePath);
    }
  };

  // Navigate to the beneficiary's profile (claimed owner / pending recipient).
  const handleBeneficiaryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (consumeGatedNavigation('connect')) return;
    if (askOnBehalf.beneficiary && isAuthenticated) {
      navigate(getProfilePath(askOnBehalf.beneficiary));
    }
  };

  /**
   * Handle opening share sheet
   */
  const handleShare = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openQuestionShareSheet({
        question,
        sharerId: userId || 'guest',
        scenario: question.is_my_question ? ShareScenario.OwnContent : ShareScenario.OthersContent,
        isOwn: question.is_my_question,
      });
    },
    [question, userId, openQuestionShareSheet],
  );

  return (
    <>
      <style>{customStyles}</style>
      <div ref={impressionContainerRef}>
        <Card className="w-full bg-neutral-50 rounded-[20px] border-none shadow-xs">
          <CardContent
            className="p-4 space-y-3 focus:outline-hidden transition-all rounded-[20px] cursor-pointer"
            onClick={handleQuestionClick}
            onKeyDown={handleQuestionKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`View post: ${question.title}`}
          >
            {/* Header */}
            {header ?? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0 overflow-hidden">
                  {/* AIDEV-NOTE: avatar by claim status (anonymous / claimed→beneficiary /
                      declined→creator / pending→stacked / normal→author) — shared with
                      QuestionDetails via <QuestionAuthorAvatar>. */}
                  <QuestionAuthorAvatar
                    view={askOnBehalf}
                    author={detail?.user ?? null}
                    isAnonymous={question.is_anonymous}
                    variant="card"
                    onAuthorClick={handleAuthorClick}
                    onBeneficiaryClick={handleBeneficiaryClick}
                  />

                  <div className="flex flex-col min-w-0">
                    {/* AIDEV-NOTE: QuestionCard redesign - name display based on claim status
                    - Claimed ask-on-behalf: show beneficiary name only (they own it now)
                    - Declined ask-on-behalf: show creator name + "Asking for someone else" (no beneficiary info)
                    - Pending ask-on-behalf: show creator name + "Asking for [beneficiary]"
                    - Normal: show user name
                */}
                    <div className="flex items-center gap-1 min-w-0">
                      {/* name by claim status — shared via <QuestionAuthorName>. */}
                      <QuestionAuthorName
                        view={askOnBehalf}
                        author={detail?.user ?? null}
                        isAnonymous={question.is_anonymous}
                        variant="card"
                        onAuthorClick={handleAuthorClick}
                        onBeneficiaryClick={handleBeneficiaryClick}
                      />
                      {showPinBadge && (
                        <PinIndicatorIcon
                          className="w-4 h-4 text-mb-teal shrink-0"
                          aria-label={
                            detail?.pinned_by_name ? `Pinned by ${detail.pinned_by_name}` : 'Pinned'
                          }
                        />
                      )}
                    </div>
                    {spaceEventItems.length > 0 ? (
                      <div className="flex items-center gap-1 min-w-0">
                        <SpaceIndicator
                          items={spaceEventItems}
                          onItemClick={(item, e) => {
                            e.stopPropagation();
                            // No gate here: opening the sheet is not navigation, and every item
                            // inside it routes through handleSpaceItemClick, which is gated.
                            // Live "at {event}" chip (L7/L12): tap goes straight
                            // to the event page (feed tab) via the shared item
                            // handler — never the spaces sheet or a feed filter.
                            if (item.type === 'event' && item.live) {
                              // §9 extension (W2-6 ruling): the chip is its own
                              // surface. Space context is the FEED's space (the
                              // boost/annotation scope), id-only — the summary
                              // carries no space object, and buildEventParentSpace-
                              // Payload is built for exactly this cache-cold shape.
                              trackEvent(ANALYTICS_EVENTS.live_surface_tapped, {
                                surface: 'at_event_chip',
                                ...buildEventContextPayload(authorLiveEvent),
                                ...buildEventParentSpacePayload({
                                  space_id: communitySpaceId ?? spaceId ?? null,
                                }),
                              });
                              handleSpaceItemClick(item);
                              return;
                            }
                            setIsSpacesSheetOpen(true);
                          }}
                        />
                        <span className="text-xs text-mb-gray-light shrink-0">·</span>
                        <span className="text-xs text-mb-gray-light shrink-0">{timeAgo}</span>
                        {helpableChip}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-mb-gray-light">
                        {detail?.topic_name && (
                          <>
                            <TopicBadge name={detail.topic_name} />
                            <span>·</span>
                          </>
                        )}
                        <span>{timeAgo}</span>
                        {helpableChip}
                      </div>
                    )}
                    {/* Asking-for subtitle (pending/declined only). `subtitle` is null
                        once CLAIMED — the beneficiary is the owner then, so the name +
                        avatar above already switch to them and a "Asking for {beneficiary}"
                        line would contradict itself. Claim attribution lives in the
                        AskedOnYourBehalfIndicator footer instead. */}
                    {askOnBehalf.subtitle && (
                      <span className="text-mb-gray-text text-xs wrap-break-word">
                        {askOnBehalf.subtitle}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center self-baseline gap-0">
                  <ShareButton onClick={handleShare} />

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={e => {
                          e.stopPropagation(); // Prevent question click event
                        }}
                        className="w-6 h-6 p-0 text-gray-400 hover:text-gray-600"
                      >
                        <MoreVerticalIcon className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 shadow-lg border-gray-200">
                      {/* Pin to community — space admins only. `communityContext`
                          guard prevents dead-UI on Home feed for platform admins
                          (handlePinClick early-returns when context is absent). */}
                      {communityContext && canManagePins && !isQuestionPinned && (
                        <DropdownMenuItem
                          onClick={handlePinClick}
                          className="text-gray-700 focus:text-gray-800 focus:bg-gray-50 cursor-pointer"
                        >
                          <PinActionIcon className="w-4 h-4 mr-2" />
                          Pin post
                        </DropdownMenuItem>
                      )}

                      {/* Unpin from community — space admins only */}
                      {communityContext && canManagePins && isQuestionPinned && (
                        <DropdownMenuItem
                          onClick={handleUnpinClick}
                          className="text-gray-700 focus:text-gray-800 focus:bg-gray-50 cursor-pointer"
                        >
                          <PinActionIcon className="w-4 h-4 mr-2" />
                          Unpin this post
                        </DropdownMenuItem>
                      )}

                      {/* Bookmark */}
                      <DropdownMenuItem
                        onClick={e => {
                          e.stopPropagation();
                          setShowComingSoonModal(true);
                        }}
                        className="text-gray-700 focus:text-gray-800 focus:bg-gray-50 cursor-pointer"
                      >
                        <BookmarkIcon className="w-4 h-4 mr-2" />
                        Bookmark
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={e => {
                          e.stopPropagation();
                          trackEvent(ANALYTICS_EVENTS.post_report_clicked, {
                            user_id: user?.id ?? null,
                            question_id: question.id,
                            is_anonymous: question.is_anonymous,
                            source_context: sourceContext,
                            position_in_feed: positionInFeed ?? null,
                          });
                          setIsReportModalOpen(true);
                        }}
                        className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer"
                      >
                        <FlagIcon className="w-4 h-4 mr-2" />
                        Report
                      </DropdownMenuItem>

                      {/* Crosspost - Creator only */}
                      {canCrosspostQuestion(user, question) &&
                        question.event_summaries.length > 0 && (
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation();
                              trackEvent(ANALYTICS_EVENTS.post_crosspost_clicked, {
                                user_id: user?.id ?? null,
                                question_id: question.id,
                                is_anonymous: question.is_anonymous,
                                source_context: sourceContext,
                                position_in_feed: positionInFeed ?? null,
                              });
                              // Show crosspost modal in-place instead of navigating
                              setIsCrosspostModalOpen(true);
                            }}
                            className="text-gray-700 focus:text-gray-800 focus:bg-gray-50 cursor-pointer"
                          >
                            <ShareIcon className="w-4 h-4 mr-2" />
                            Crosspost to other events
                          </DropdownMenuItem>
                        )}

                      {/* Edit - Creator only */}
                      {canEditQuestion(user, question) && (
                        <DropdownMenuItem
                          onClick={e => {
                            trackEvent(ANALYTICS_EVENTS.post_edit_clicked, {
                              user_id: user?.id ?? null,
                              question_id: question.id,
                              is_anonymous: question.is_anonymous,
                              source_context: sourceContext,
                              position_in_feed: positionInFeed ?? null,
                            });
                            e.stopPropagation();
                            if (consumeGatedNavigation('generic')) return;
                            const editState: EditPostLocationState = { question };
                            navigate(getQuestionEditPath(question), { state: editState });
                          }}
                          className="text-gray-700 focus:text-gray-800 focus:bg-gray-50 cursor-pointer"
                        >
                          <EditIcon className="w-4 h-4 mr-2" />
                          Edit post
                        </DropdownMenuItem>
                      )}

                      {/* Mark as Resolved - Question author only */}
                      {/* {isOwnQuestion && (
                    <DropdownMenuItem
                      onClick={handleMarkAsAnswered}
                      className="text-green-600 focus:text-green-700 focus:bg-green-50 cursor-pointer"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                      Mark as Resolved
                    </DropdownMenuItem>
                  )} */}

                      {/* Delete Question - Owner or admin */}
                      {canDeleteQuestion(user, question) && (
                        <DropdownMenuItem
                          onClick={handleDeleteQuestion}
                          className="text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer"
                        >
                          <Trash2Icon className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="space-y-0.5">
              <div className="space-y-1">
                <h2
                  dir="auto"
                  className="font-semibold text-black text-base leading-tight wrap-break-word"
                >
                  {question.title}
                </h2>
                {/* plainTextOnly: the card is a preview, not the post. Markdown
                    syntax is stripped rather than rendered, and a bare URL stays
                    readable but inert — so tapping anywhere in the body opens the
                    question instead of navigating away to the link. */}
                <RichTextDisplay
                  content={question.content}
                  className="text-black text-sm leading-relaxed"
                  collapseNewlines={true}
                  maxLines={3}
                  plainTextOnly={true}
                />
              </div>

              {/* Tags - removed since not in current API */}

              {/* Image - removed since not in current API */}
            </div>

            {/* AIDEV-NOTE: Claim/Decline actions for pending questions asked for this user */}
            {/* {question.claim_status === ClaimStatus.Pending && question.asked_for_user_id === user?.id && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex-1 text-sm text-blue-800">
                <strong>{question.created_by_user?.full_name || 'Someone'}</strong> made a post for you.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClaimQuestion}
                disabled={isClaimDeclinePending}
                className="bg-green-500 text-white border-green-500 hover:bg-green-600 hover:border-green-600"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Claim
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeclineQuestion}
                disabled={isClaimDeclinePending}
                className="bg-white text-red-600 border-red-300 hover:bg-red-50"
              >
                <UserX className="w-4 h-4 mr-1" />
                Decline
              </Button>
            </div>
          )} */}

            {/* AIDEV-NOTE: Resolved status badge */}
            {/* {question.status === QuestionStatus.resolved && (
            <div className="flex justify-center mb-3">
              <Badge className="bg-green-50 text-green-700 border-none hover:bg-green-50 text-xs px-3 py-1 rounded-full font-medium shadow-none">
                <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5" />
                Resolved
              </Badge>
            </div>
          )} */}

            {/* Actions */}
            <QuestionInteractionRow
              upliftsCount={question.uplifts_count || 0}
              meTooCount={question.me_too_count || 0}
              iCanHelpCount={question.i_can_help_count || 0}
              userHasUpvoted={userHasUpvoted}
              userHasMeToo={userHasMeToo}
              userHasICanHelp={userHasICanHelp}
              upliftLimitReached={upliftLimitReached}
              isOwnQuestion={isOwnQuestion}
              isPending={createPending || deletePending || interactionsLoading}
              onUpvote={() => {
                if (onInteractionBlocked) {
                  onInteractionBlocked();
                  return;
                }
                if (!isAuthenticated) {
                  openLoginPrompt('upvote');
                  return;
                }
                trackEvent(ANALYTICS_EVENTS.reaction_sent, {
                  type: 'uplift',
                  question_id: question.id,
                  author_id: analyticsAuthorId,
                  is_anonymous: question.is_anonymous,
                  source_context: sourceContext,
                  position_in_feed: positionInFeed ?? null,
                  ...buildSpaceContextPayload(question.space_summaries),
                });
                trackMeaningfulAction('reaction_sent', {
                  type: 'uplift',
                  question_id: question.id,
                });
                handleUpvote();
              }}
              onMeToo={() => {
                if (onInteractionBlocked) {
                  onInteractionBlocked();
                  return;
                }
                if (!isAuthenticated) {
                  openLoginPrompt('me_too');
                  return;
                }
                trackEvent(ANALYTICS_EVENTS.reaction_sent, {
                  type: 'me_too',
                  question_id: question.id,
                  author_id: analyticsAuthorId,
                  is_anonymous: question.is_anonymous,
                  source_context: sourceContext,
                  position_in_feed: positionInFeed ?? null,
                  ...buildSpaceContextPayload(question.space_summaries),
                });
                trackMeaningfulAction('reaction_sent', {
                  type: 'me_too',
                  question_id: question.id,
                });
                handleMeToo();
              }}
              onCanHelp={() => {
                // Gated first: handleCanHelp navigates into the chat route, so unlike uplift and
                // me-too this action is one of the gate's targets.
                if (consumeGatedNavigation('i_can_help')) return;
                if (onInteractionBlocked) {
                  onInteractionBlocked();
                  return;
                }
                if (!isAuthenticated) {
                  openLoginPrompt('i_can_help');
                  return;
                }
                handleCanHelp();
              }}
            />
          </CardContent>
          {shouldShowAskedOnYourBehalfIndicator && (
            <CardFooter className="p-0 block relative">
              <AskedOnYourBehalfIndicator
                view={askOnBehalf}
                onNavigationBlocked={
                  blockNavigation
                    ? () => {
                        consumeGatedNavigation('connect');
                      }
                    : undefined
                }
              />
            </CardFooter>
          )}
        </Card>
      </div>

      <ReportDialog
        open={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        contentLabel="post"
        isSubmitting={reportMutation.isPending}
        onSubmit={(reasons, details) => {
          trackEvent(ANALYTICS_EVENTS.post_report_submitted, {
            user_id: user?.id ?? null,
            question_id: question.id,
            report_reasons: reasons,
            report_details: details,
            is_anonymous: question.is_anonymous,
            source_context: sourceContext,
            position_in_feed: positionInFeed ?? null,
          });
          reportMutation.mutate(
            {
              contentType: 'question',
              contentId: question.id,
              reasons,
              details: details || null,
            },
            {
              onSuccess: () => {
                setIsReportModalOpen(false);
              },
            },
          );
        }}
      />

      {/* Coming soon modal */}
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => {
          setShowComingSoonModal(false);
        }}
        featureName="Bookmark a post"
        description="We're working on a feature to bookmark your favorite posts!"
        IconComponent={BookmarkIcon}
      />

      {/* Crosspost modal */}
      {question.event_summaries.length > 0 && (
        <CrosspostModal
          isOpen={isCrosspostModalOpen}
          onClose={() => {
            setIsCrosspostModalOpen(false);
          }}
          onSuccess={() => {
            // Refresh question data after crosspost
            invalidateQuestionCachesCentral({
              queryClient,
              eventIds: relatedEventIds,
              shouldInvalidateMeetverse,
            });
          }}
          question={{
            id: question.id,
            title: question.title,
            content: question.content,
            is_anonymous: question.is_anonymous,
            is_featured: question.is_featured,
            is_official: question.is_official,
            events: question.event_summaries.map(e => ({ id: e.id, name: e.name })),
          }}
        />
      )}

      {/* Login Prompt Modal for unauthenticated users */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={closeLoginPrompt}
        actionType={loginPromptActionType}
        onLogin={handleLogin}
      />

      <ConfirmDeleteDrawer
        open={isDeleteDrawerOpen}
        onOpenChange={setIsDeleteDrawerOpen}
        title="Delete this post?"
        description="This action cannot be undone."
        confirmLabel="Delete post"
        isConfirming={deleteQuestionMutation.isPending}
        onConfirm={handleConfirmDeleteQuestion}
      />

      {communityContext && (
        <>
          <PinConfirmDrawer
            open={isPinDrawerOpen}
            mode="pin"
            spaceName={communityContext.spaceName}
            isLoading={isPinning}
            currentPinCount={currentPinCount}
            onConfirm={() => {
              void handleConfirmPin();
            }}
            onOpenChange={setIsPinDrawerOpen}
          />
          <PinConfirmDrawer
            open={isUnpinDrawerOpen}
            mode="unpin"
            spaceName={communityContext.spaceName}
            isLoading={isUnpinning}
            onConfirm={() => {
              void handleConfirmUnpin();
            }}
            onOpenChange={setIsUnpinDrawerOpen}
          />
        </>
      )}

      {isSpacesSheetOpen && (
        <SpacesSheet
          items={spaceEventItems}
          open={isSpacesSheetOpen}
          onClose={() => {
            setIsSpacesSheetOpen(false);
          }}
          onItemClick={handleSpaceItemClick}
        />
      )}
    </>
  );
});
